import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [busy,     setBusy]     = useState(false);
  const { login } = useAuth();
  const { t, lang, toggle } = useLanguage();
  const navigate  = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password.trim()) {
      setError(t('login.errorRequired'));
      return;
    }
    setBusy(true);
    try {
      await login(username.trim(), password.trim());
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? t('login.errorInvalid');
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-navy flex flex-col items-center justify-center p-4 md:p-6"
      style={{ outline: '1px solid #C9A84C', outlineOffset: '-1px' }}
    >
      {/* Lang toggle */}
      <div className="absolute top-4 right-4">
        <button
          onClick={toggle}
          className="rounded border border-gold/40 px-2.5 py-1 text-[11px] font-bold text-gold/60 transition hover:border-gold hover:text-gold"
          title={lang === 'en' ? 'Switch to Tamil' : 'Switch to English'}
        >
          {lang === 'en' ? 'தமிழ்' : 'EN'}
        </button>
      </div>

      {/* Header — photo medallion + church name */}
      <div className="flex flex-col items-center mb-8 select-none">
        <div
          className="w-20 h-20 rounded-full border-2 flex items-center justify-center mb-3 overflow-hidden bg-navy-light"
          style={{ borderColor: '#C9A84C' }}
        >
          <img
            src="/images/mary-fatima.png"
            alt="Our Lady of Fatima"
            className="w-full h-full object-cover"
            onError={(e) => {
              const img = e.currentTarget;
              img.style.display = 'none';
              const fallback = img.nextElementSibling as HTMLElement | null;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <span
            className="text-gold items-center justify-center w-full h-full"
            style={{ display: 'none', fontSize: '2rem', lineHeight: 1 }}
          >
            ✝
          </span>
        </div>
        <h1 className="text-gold font-georgia font-bold text-xl">
          {t('login.churchName')}
        </h1>
        <p className="font-georgia text-sm mt-1" style={{ color: '#b4c6d4' }}>
          {t('login.location')}
        </p>
      </div>

      {/* Login card */}
      <div className="bg-white w-full max-w-sm shadow-card relative">
        {/* Teal top accent bar */}
        <div className="h-1 bg-teal" />

        <form onSubmit={handleSubmit} className="px-8 py-7">
          {/* Username */}
          <div className="mb-5">
            <label className="label">{t('login.username')}</label>
            <input
              type="text"
              value={username}
              autoFocus
              autoComplete="username"
              onChange={(e) => setUsername(e.target.value)}
              className="input-field"
            />
          </div>

          {/* Password */}
          <div className="mb-5">
            <label className="label">{t('login.password')}</label>
            <input
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
            />
          </div>

          {/* Error banner */}
          {error && (
            <div className="mb-4 px-3 py-2 bg-overdue-bg border border-overdue-text text-overdue-text text-sm font-georgia text-center">
              {error}
            </div>
          )}

          {/* Sign In button */}
          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full py-3 mt-1 text-base"
          >
            {busy ? t('login.signingIn') : t('login.signIn')}
          </button>

          <p className="mt-5 text-center font-georgia italic text-xs" style={{ color: '#8ca0af' }}>
            {t('login.credentials')}
          </p>
        </form>
      </div>

      {/* Footer */}
      <p className="mt-8 font-georgia italic text-xs" style={{ color: '#546474' }}>
        {t('login.copyright')}
      </p>
    </div>
  );
}
